import { IS_DEMO_MODE } from '../config/env';
import { supabase } from '../lib/supabase';
import { SHOWCASE_WORKSPACE_ID } from '../demo/showcaseCatalog';

export type OnboardingStepKey =
  | 'workspace_confirm'
  | 'profile_name'
  | 'terms_ack'
  | 'privacy_ack'
  | 'mfa_enroll';

export interface OnboardingContext {
  workspaceId: string;
  workspaceName: string;
  policyVersion: string;
}

export interface OnboardingState {
  stateId: string;
  workspaceId: string;
  policyVersion: string;
  requiredSteps: OnboardingStepKey[];
  completedSteps: OnboardingStepKey[];
  status: 'in_progress' | 'completed' | 'invalidated' | 'cancelled';
  isComplete: boolean;
}

interface StepCompleteInput {
  workspaceId: string;
  stepKey: OnboardingStepKey;
  policyId?: string;
  policyVersion?: string;
}

const DEMO_CONTEXTS: OnboardingContext[] = [
  {
    workspaceId: 'demo-workspace',
    workspaceName: 'Demo Workspace',
    policyVersion: 'v1',
  },
];

const demoStates = new Map<string, OnboardingState>();
const DEMO_ONBOARDED_WORKSPACES = new Set([SHOWCASE_WORKSPACE_ID]);

function mapError(error: unknown): string {
  const message = error instanceof Error ? error.message : 'onboarding_unknown_error';
  if (message.includes('onboarding_workspace_context_required')) return 'workspace_context_required';
  if (message.includes('onboarding_no_accessible_workspace')) return 'no_accessible_workspace';
  if (message.includes('onboarding_not_authenticated')) return 'not_authenticated';
  if (message.includes('onboarding_workspace_membership_denied')) return 'workspace_membership_denied';
  if (message.includes('onboarding_required_steps_incomplete')) return 'required_steps_incomplete';
  if (message.includes('onboarding_step_not_required')) return 'step_not_required';
  if (message.includes('onboarding_step_invalid')) return 'step_invalid';
  if (message.includes('onboarding_account_suspended')) return 'account_suspended';
  return message;
}

function mapState(data: Record<string, unknown>): OnboardingState {
  return {
    stateId: String(data.state_id ?? ''),
    workspaceId: String(data.workspace_id ?? ''),
    policyVersion: String(data.policy_version ?? 'v1'),
    requiredSteps: ((data.required_steps as unknown[]) ?? []) as OnboardingStepKey[],
    completedSteps: ((data.completed_steps as unknown[]) ?? []) as OnboardingStepKey[],
    status: (data.status as OnboardingState['status']) ?? 'in_progress',
    isComplete: Boolean(data.is_complete),
  };
}

class OnboardingService {
  async getContexts(): Promise<OnboardingContext[]> {
    if (IS_DEMO_MODE) {
      return DEMO_CONTEXTS;
    }

    const { data, error } = await supabase.rpc('get_onboarding_contexts_secure');
    if (error) {
      throw new Error(mapError(error));
    }

    const contexts = ((data as { contexts?: Array<Record<string, unknown>> } | null)?.contexts ?? []).map((row) => ({
      workspaceId: String(row.workspace_id ?? ''),
      workspaceName: String(row.workspace_name ?? ''),
      policyVersion: String(row.policy_version ?? 'v1'),
    }));

    return contexts;
  }

  async initialize(workspaceId?: string): Promise<OnboardingState> {
    if (IS_DEMO_MODE) {
      const key = workspaceId ?? DEMO_CONTEXTS[0].workspaceId;
      const existing = demoStates.get(key);
      if (existing) return existing;
      const isComplete = DEMO_ONBOARDED_WORKSPACES.has(key);

      const state: OnboardingState = {
        stateId: `demo-onboarding-${key}`,
        workspaceId: key,
        policyVersion: 'v1',
        requiredSteps: ['workspace_confirm'],
        completedSteps: isComplete ? ['workspace_confirm'] : [],
        status: isComplete ? 'completed' : 'in_progress',
        isComplete,
      };
      demoStates.set(key, state);
      return state;
    }

    const { data, error } = await supabase.rpc('initialize_onboarding_secure', {
      p_workspace_id: workspaceId ?? null,
    });
    if (error) {
      throw new Error(mapError(error));
    }

    return mapState((data as Record<string, unknown>) ?? {});
  }

  async completeStep(input: StepCompleteInput): Promise<OnboardingState> {
    if (IS_DEMO_MODE) {
      const current = await this.initialize(input.workspaceId);
      const completed = current.completedSteps.includes(input.stepKey)
        ? current.completedSteps
        : [...current.completedSteps, input.stepKey];
      const isComplete = current.requiredSteps.every((step) => completed.includes(step));
      const next: OnboardingState = {
        ...current,
        completedSteps: completed,
        isComplete,
        status: isComplete ? 'completed' : 'in_progress',
      };
      demoStates.set(input.workspaceId, next);
      return next;
    }

    const { data, error } = await supabase.rpc('complete_onboarding_step_secure', {
      p_workspace_id: input.workspaceId,
      p_step_key: input.stepKey,
      p_policy_id: input.policyId ?? null,
      p_policy_version: input.policyVersion ?? null,
    });
    if (error) {
      throw new Error(mapError(error));
    }

    return mapState((data as Record<string, unknown>) ?? {});
  }

  async complete(workspaceId: string): Promise<OnboardingState> {
    if (IS_DEMO_MODE) {
      const current = await this.initialize(workspaceId);
      const next: OnboardingState = {
        ...current,
        completedSteps: [...current.requiredSteps],
        status: 'completed',
        isComplete: true,
      };
      demoStates.set(workspaceId, next);
      return next;
    }

    const { data, error } = await supabase.rpc('complete_onboarding_secure', {
      p_workspace_id: workspaceId,
    });
    if (error) {
      throw new Error(mapError(error));
    }

    return mapState((data as Record<string, unknown>) ?? {});
  }

  async reset(workspaceId: string): Promise<OnboardingState> {
    if (IS_DEMO_MODE) {
      const current = await this.initialize(workspaceId);
      const next: OnboardingState = {
        ...current,
        completedSteps: [],
        status: 'in_progress',
        isComplete: false,
      };
      demoStates.set(workspaceId, next);
      return next;
    }

    const { data, error } = await supabase.rpc('reset_onboarding_secure', {
      p_workspace_id: workspaceId,
    });
    if (error) {
      throw new Error(mapError(error));
    }

    return mapState((data as Record<string, unknown>) ?? {});
  }
}

export const onboardingService = new OnboardingService();
